import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * How long to give the OS to switch away from the browser before assuming the
 * deep link went nowhere. Long enough to cover Safari's "Open in TeamHub?"
 * confirmation, short enough that the hint is still useful.
 */
const APP_LAUNCH_GRACE_MS = 2000;

interface AppLaunchHint {
  isHintVisible: boolean;
  /** Call right after navigating to the deep link. */
  watchAppLaunch: () => void;
}

/**
 * No API reports whether a native app is installed — browsers removed that
 * because it fingerprints the device. The only available signal is whether the
 * tab left the foreground shortly after the link fired, which is a heuristic.
 * That is why the result drives an inline hint rather than an automatic App
 * Store redirect: a false positive (slow tap on Safari's confirmation dialog,
 * slow cold start) then costs nothing instead of yanking the user out of the
 * app that was opening.
 */
export function useAppLaunchHint(): AppLaunchHint {
  const [isHintVisible, setIsHintVisible] = useState(false);

  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => () => cleanupRef.current?.(), []);

  const watchAppLaunch = useCallback(() => {
    cleanupRef.current?.();
    setIsHintVisible(false);

    let leftForeground = false;

    const markLeftForeground = () => {
      leftForeground = true;
    };

    // `visibilitychange` fires on the way back too, so only a hidden document
    // counts as the app having taken over.
    const onVisibilityChange = () => {
      if (document.hidden) {
        markLeftForeground();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', markLeftForeground);

    const timeoutId = window.setTimeout(() => {
      cleanupRef.current?.();
      if (!leftForeground) {
        setIsHintVisible(true);
      }
    }, APP_LAUNCH_GRACE_MS);

    cleanupRef.current = () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', markLeftForeground);
      cleanupRef.current = null;
    };
  }, []);

  return { isHintVisible, watchAppLaunch };
}
